import { privateRequest } from "../axios";
import type { ExportFileResult, ListApiResponse } from "@/types/common";
import { parseFilenameFromContentDisposition } from "@/lib/downloadExcel";

// Real endpoint (GET /api/admin/payments), given directly by the user;
// response shape confirmed via a live authenticated request rather than
// assumed — see docs/decisions.md #89. A flat payment-transaction log,
// not a subscription/billing record: there is no room/started/renews/
// auto-renew field anywhere on it (pages/Payments/Payments.tsx's table
// shows those as clearly-marked decorative columns, per the user, since
// the requested screenshot was a subscription-summary design this
// endpoint doesn't back).
export interface Payment {
  id: number;
  paymentStatus: string;
  amount: number;
  refId: string;
  paymentId: string;
  paymentType: string | null;
  paymentProvider?: string | null;
  userCategory?: string | null;
  userType: string;
  createdAt: string;
  subscriptionStartedAt?: string | null;
  renewsAt?: string | null;
  endsAt?: string | null;
  autoRenewal?: boolean | null;
  firstName: string;
  lastName: string;
  message: string | null;
  refundStatus: string;
}

export interface GetPaymentsParams {
  pageNo?: number;
  perPage?: number;
  // Not independently confirmed on this endpoint — extended on the same
  // optimistic convention Members.tsx/Support.tsx's own `search` already
  // uses, and the same `isExport: true`-returns-a-file convention
  // confirmed for `GET /admin/action-logs` (see docs/decisions.md #74).
  search?: string | undefined;
  isExport?: boolean | undefined;
}

const ENDPOINTS = {
  PAYMENTS: "/payments",
};

const getPayments = async (params?: GetPaymentsParams) => {
  const response = await privateRequest.get<ListApiResponse<Payment>>(ENDPOINTS.PAYMENTS, { params });
  return response.data;
};

const exportPayments = async (params?: Omit<GetPaymentsParams, "isExport">): Promise<ExportFileResult> => {
  const response = await privateRequest.get(ENDPOINTS.PAYMENTS, {
    params: { ...params, isExport: true },
    responseType: "blob",
  });
  return {
    blob: response.data as Blob,
    fileName: parseFilenameFromContentDisposition(response.headers["content-disposition"]),
  };
};

const paymentService = {
  getPayments,
  exportPayments,
};

export default paymentService;
